import { useEffect, useState } from "react";
import { ThumbsUp, ThumbsDown, Check, EyeOff } from "lucide-react";
import { api } from "@/lib/api";

/**
 * RecommendationOutcomes
 * ----------------------
 * After the advisor renders a reveal, fetch the structured recommendations
 * that the platform persisted for THIS session and let the user mark how
 * each one landed. The outcomes feed `/api/intelligence/recommendations/
 * {id}/outcome`, which powers the effectiveness rollup — the moat metric.
 *
 * UX principle: one tiny strip per recommendation type. Soft, dignified,
 * never demanding. The user can ignore it entirely.
 */
const TYPE_LABEL = {
  suggested_approach: "the suggested approach",
  what_to_avoid:      "the things-to-avoid",
  message_draft:      "the draft message",
  interpretive_read:  "the read beneath the surface",
  emotional_framing:  "the emotional framing",
};

const OUTCOME_OPTIONS = [
  { key: "helpful",     label: "Landed",       icon: ThumbsUp,   tone: "sage" },
  { key: "tried",       label: "I'll try it",  icon: Check,      tone: "mauve" },
  { key: "not_helpful", label: "Didn't land",  icon: ThumbsDown, tone: "rose" },
  { key: "ignored",     label: "Not for me",   icon: EyeOff,     tone: "cocoa" },
];

export function RecommendationOutcomes({ sessionId }) {
  const [recs, setRecs] = useState([]);
  const [saving, setSaving] = useState({});       // {rec_id: bool}
  const [picks, setPicks] = useState({});         // {rec_id: outcome}
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    // Recommendations are persisted in fire-and-forget pipeline. Poll up to
    // 3 times with a small backoff to catch the writes.
    let attempt = 0;
    const fetchOnce = async () => {
      try {
        const r = await api.get(`/intelligence/recommendations?session_id=${encodeURIComponent(sessionId)}&limit=10`);
        const items = r?.data?.recommendations || [];
        if (cancelled) return;
        if (items.length > 0) {
          setRecs(items);
          // Pre-fill picks for any rec that already has an outcome
          const initial = {};
          items.forEach((it) => { if (it.outcome) initial[it.id] = it.outcome; });
          setPicks(initial);
          setLoaded(true);
          return;
        }
        attempt += 1;
        if (attempt < 3) setTimeout(fetchOnce, 1500 * attempt);
        else setLoaded(true);
      } catch {
        if (!cancelled) setLoaded(true);
      }
    };
    fetchOnce();
    return () => { cancelled = true; };
  }, [sessionId]);

  const pick = async (rec, outcome) => {
    if (saving[rec.id]) return;
    setSaving((s) => ({ ...s, [rec.id]: true }));
    const prev = picks[rec.id];
    setPicks((p) => ({ ...p, [rec.id]: outcome }));
    try {
      await api.post(`/intelligence/recommendations/${rec.id}/outcome`, { outcome });
    } catch {
      // Revert pick on failure — soft, no toast spam
      setPicks((p) => ({ ...p, [rec.id]: prev || null }));
    } finally {
      setSaving((s) => ({ ...s, [rec.id]: false }));
    }
  };

  if (!loaded || recs.length === 0) return null;

  return (
    <div
      data-testid="rec-outcomes"
      className="ss-card-premium p-5 sm:p-6 mt-6"
      style={{ background: "linear-gradient(135deg, rgba(247,237,229,0.6), rgba(255,251,247,0.9))" }}
    >
      <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">How did this land?</p>
      <p className="mt-2 text-cocoa/70 text-[13px] leading-relaxed italic">
        Quick honesty here teaches SugarSense how to read you better next time.
        Skip anything that doesn't apply.
      </p>

      <div className="mt-4 space-y-3">
        {recs.map((rec) => {
          const label = TYPE_LABEL[rec.recommendation_type] || rec.recommendation_type;
          const current = picks[rec.id];
          return (
            <div
              key={rec.id}
              data-testid={`rec-outcome-row-${rec.recommendation_type}`}
              className="bg-ivory/70 border border-champagne rounded-md p-3"
            >
              <p className="text-cocoa/85 text-[13px]">
                On <span className="font-medium text-cocoa">{label}</span>:
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {OUTCOME_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  const active = current === opt.key;
                  return (
                    <button
                      key={opt.key}
                      onClick={() => pick(rec, opt.key)}
                      disabled={!!saving[rec.id]}
                      data-testid={`rec-outcome-${rec.recommendation_type}-${opt.key}`}
                      className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-[12px] transition-all duration-200 border ${
                        active
                          ? `bg-${opt.tone}/15 border-${opt.tone} text-cocoa`
                          : "bg-ivory border-champagne text-cocoa/65 hover:border-mauve/60 hover:text-cocoa"
                      } disabled:opacity-50`}
                    >
                      <Icon size={12} />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default RecommendationOutcomes;
